import { FinancialWorkspaceValueObjectsFinanceTypeEnum } from 'generated/resources/graphql'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'

import { useFinancialWorkspace } from 'modules/finance/components/FinancialWorkspace/Providers/Provider'
import { useAvailabilities } from 'modules/finance/components/FinancialWorkspaceEmployeeCosts/Providers/Availabilities'
import { PayrollUpsell } from 'modules/finance/components/FinancialWorkspaceEmployeeCosts/components/Table/components/upsells/Payroll'
import {
  FinancialWorkspaceResource,
  HookData,
} from 'modules/finance/components/FinancialWorkspaceEmployeeCosts/types/data'

import { FinanceFactSummary, formatData, useFinanceSummaries } from './useFinanceSummaries'
import { useSourcesVisibility } from './useSourcesVisibility'

interface Props {
  skip?: boolean
  level?: number
  resource?: FinancialWorkspaceResource
}

//TODO: Rename to useLaborCostsData, we use payroll data or compensations data for generating the labor costs
export const usePayrollData: (props?: Props) => HookData = (props) => {
  const { hasComparisonDate } = useFinancialWorkspace()
  const { canSeeLaborCosts } = useSourcesVisibility()
  const { availabilities, loading: loadingData } = useAvailabilities()

  const skip = !!props?.skip || !availabilities.canSeeLaborCosts

  const financeTypes: FinancialWorkspaceValueObjectsFinanceTypeEnum[] =
    availabilities.canSeeCompensations ? ['compensations'] : ['payroll']

  const { data, loading, fetchFinanceSummaries, refetch } = useFinanceSummaries({
    skip,
    financeTypes,
    resource: props?.resource,
  })

  const {
    data: comparisonData,
    loading: comparisonLoading,
    refetch: comparisonRefetch,
    fetchFinanceSummaries: fetchComparisonFinanceSummaries,
  } = useFinanceSummaries({
    skip: skip,
    financeTypes,
    compareTo: true,
    resource: props?.resource,
  })

  useUnsafeEffect(() => {
    if (!skip && !loadingData) {
      fetchFinanceSummaries()
      if (hasComparisonDate) fetchComparisonFinanceSummaries()
    }
  }, [fetchFinanceSummaries, loadingData, skip, hasComparisonDate, fetchComparisonFinanceSummaries])

  const seeUpsell = availabilities.seePayrollLanding || !availabilities.canSeeLaborCosts

  const response = useUnsafeCallback(() => {
    if (seeUpsell && !loadingData) {
      return [
        {
          id: `payroll_data`,
          customRow: {
            component: PayrollUpsell,
            visible: true,
          },
          data: [],
        },
      ]
    }

    const payrollData: FinanceFactSummary = data?.[0]
    const payrollComparisonData: FinanceFactSummary = comparisonData?.[0]
    return formatData({
      id: `payroll_data`,
      label: payrollData?.label ?? '',
      comparisonData: payrollComparisonData,
      data: payrollData,
      loading: loading || (hasComparisonDate && comparisonLoading),
    })
  }, [data, loading, seeUpsell, loadingData, comparisonData, hasComparisonDate, comparisonLoading])

  const onRefetch = useUnsafeCallback(() => {
    refetch()
    if (hasComparisonDate) comparisonRefetch()
  }, [refetch, comparisonRefetch, hasComparisonDate])

  if (!seeUpsell && !canSeeLaborCosts) {
    return
  }

  return {
    data: response(),
    refetch: onRefetch,
    visible: !!canSeeLaborCosts,
    loading: loading || (hasComparisonDate && comparisonLoading),
  }
}
